import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router';
import { formatCellValue } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { PageBanner } from '@/components/PageBanner';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { PageHeader } from '@/components/PageHeader';
import { LimitMeter } from '@/components/LimitMeter';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CreditCard,
  Bell,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Users,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Calendar,
  ExternalLink,
  Sparkles,
  Check,
  X,
  Zap,
  Tags,
  Shield,
  History,
  FileText,
  FileQuestion,
  BookOpen,
  MessageSquare,
  TrendingUp,
  Star,
  Trophy,
  Building2,
  RefreshCw,
  XCircle,
  LayoutGrid,
  List,
  Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getStripe, buildStripeAppearance } from '@/lib/stripeElements';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_DESCRIPTIONS = {
  Starter: 'For small teams',
  Growth: 'For growing organizations',
  Enterprise: 'Custom limits, SLA, SSO, and negotiated terms',
};

function titleize(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const AMBER = 'bg-amber-500/10 text-amber-600 border-amber-200';
const RED = 'bg-red-500/10 text-red-600 border-red-200';
const GREEN = 'bg-emerald-500/10 text-emerald-600 border-emerald-200';
const GRAY = 'bg-gray-500/10 text-gray-600 border-gray-200';

const StatusBadge = ({ status }) => {
  const variants = {
    trial: { label: 'Trial', className: AMBER },
    trial_expired: { label: 'Trial Expired', className: RED },
    active: { label: 'Active', className: GREEN },
    grace: { label: 'Access Ending', className: AMBER },
    cancelling: { label: 'Canceling', className: AMBER },
    cancelled: { label: 'Cancelled', className: GRAY },
    past_due: { label: 'Past Due', className: AMBER },
    unpaid: { label: 'Unpaid', className: AMBER },
    suspended: { label: 'Suspended', className: RED },
    terminated: { label: 'Terminated', className: RED },
    expired: { label: 'Expired', className: RED },
    payment_required: { label: 'Payment required', className: AMBER },
    // Alias: the raw (un-normalized) status string is 'pending_payment'; this key is
    // the fallback path when a response has no `subscription.display_status` yet.
    pending_payment: { label: 'Payment required', className: AMBER },
    deactivated: { label: 'Deactivated', className: RED },
    configuration_required: { label: 'Setup Required', className: AMBER },
    // Payment-history rows (StatusBadge is shared with the Payment History table)
    paid: { label: 'Paid', className: GREEN },
    pending: { label: 'Pending', className: AMBER },
  };
  // A status this map doesn't know gets a neutral, honest label instead of being
  // silently mislabeled — the fallback used to be `variants.trial`, which is why a
  // terminated/past_due/incomplete tenant used to render an amber "Trial" badge.
  const variant = variants[status] || { label: titleize(status) || 'Unknown', className: GRAY };
  return <Badge variant="outline" className={variant.className}>{variant.label}</Badge>;
};

function formatCompactNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-US').format(number);
}

function formatTokens(value) {
  if (value === null || value === undefined) return 'Unlimited';
  const number = Number(value || 0);
  if (number >= 1000000) return `${(number / 1000000).toFixed(number >= 10000000 ? 0 : 1)}M`;
  if (number >= 1000) return `${(number / 1000).toFixed(number >= 10000 ? 0 : 1)}K`;
  return formatCompactNumber(number);
}

function formatLimitValue(value, formatter = formatCompactNumber) {
  if (value === -1 || value === null || value === undefined) return 'Unlimited';
  return formatter(value);
}

const EntitlementTableColumns = () => (
  <colgroup>
    <col className="w-[40%]" />
    <col className="w-[12%]" />
    <col className="w-[12%]" />
    <col className="w-[24%]" />
    <col className="w-[12%]" />
  </colgroup>
);

function formatRemainingPercent(remaining, limit) {
  const limitValue = Number(limit || 0);
  if (limitValue <= 0) return null;
  const percent = Math.max(0, (Number(remaining || 0) / limitValue) * 100);
  return Number.isInteger(percent) ? `${percent.toFixed(0)}%` : `${percent.toFixed(1)}%`;
}

// Default fallback tiers (used only if API fails)
const DEFAULT_PRICING_TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    description: PLAN_DESCRIPTIONS.Starter,
    monthlyPrice: 8,
    annualPrice: 7.20,
    icon: Zap,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    content_team_limit: 5,
    employee_limit: 100,
    document_limit: 100,
    max_chunks_per_document: 50,
    topics_limit: 30,
    content_gap_limit: 100,
    integrations_limit: 2,
    monthly_export_limit: 5,
    scheduled_reports_limit: 5,
    notification_limit: 10,
    monthly_token_limit: 252000,
    conversation_monthly_token_limit: 250000,
    kb_monthly_token_limit: 2000,
    monthly_cost_limit_usd: 100,
    tokens_per_seat_month: 2500,
    user_daily_capacity_pct: 0.1167,
    user_weekly_capacity_pct: 0.70,
    user_monthly_capacity_pct: 0.70,
    features: [],
  },
  {
    id: 'growth',
    name: 'Growth',
    description: PLAN_DESCRIPTIONS.Growth,
    monthlyPrice: 10,
    annualPrice: 9,
    icon: TrendingUp,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    popular: true,
    content_team_limit: 20,
    employee_limit: 750,
    document_limit: 500,
    max_chunks_per_document: 100,
    topics_limit: 50,
    content_gap_limit: 500,
    integrations_limit: 5,
    monthly_export_limit: 25,
    scheduled_reports_limit: 25,
    notification_limit: 50,
    monthly_token_limit: 2817500,
    conversation_monthly_token_limit: 2812500,
    kb_monthly_token_limit: 5000,
    monthly_cost_limit_usd: 1500,
    tokens_per_seat_month: 3750,
    user_daily_capacity_pct: 0.0156,
    user_weekly_capacity_pct: 0.0933,
    user_monthly_capacity_pct: 0.0933,
    features: [],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: PLAN_DESCRIPTIONS.Enterprise,
    monthlyPrice: null,
    annualPrice: null,
    icon: Shield,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    content_team_limit: null,
    employee_limit: null,
    document_limit: null,
    max_chunks_per_document: null,
    topics_limit: null,
    integrations_limit: null,
    monthly_export_limit: null,
    scheduled_reports_limit: null,
    notification_limit: null,
    monthly_token_limit: null,
    monthly_cost_limit_usd: null,
    tokens_per_seat_month: null,
    user_daily_capacity_pct: null,
    user_weekly_capacity_pct: null,
    user_monthly_capacity_pct: null,
    features: [],
  },
];

const normalizeTierId = (value) => (value || '').toString().trim().toLowerCase().replace(/\s+/g, '_');

export default function BillingPage() {
  const { user, token, tenant, refreshTenant } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const processedCheckoutSessionRef = useRef(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pricingTiers, setPricingTiers] = useState(DEFAULT_PRICING_TIERS);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState('starter');
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [credits, setCredits] = useState({ balance: 0, can_apply_to_checkout: false });
  const [applyCredits, setApplyCredits] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [renewalPaymentMethod, setRenewalPaymentMethod] = useState(null);
  const [renewalReady, setRenewalReady] = useState(false);
  const [checkoutQuote, setCheckoutQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [enterpriseQuote, setEnterpriseQuote] = useState(null);
  const [enterpriseQuoteLoading, setEnterpriseQuoteLoading] = useState(false);
  const [enterpriseEmployeeCount, setEnterpriseEmployeeCount] = useState('');
  const [checkoutSeatCount, setCheckoutSeatCount] = useState('1');
  const [pricingStep, setPricingStep] = useState('plan');
  const [collapsedEntitlementSections, setCollapsedEntitlementSections] = useState({});
  const [entitlementLayout, setEntitlementLayout] = useState('table');
  const [includedEntitlementTab, setIncludedEntitlementTab] = useState('knowledge-base');

  // Embedded Stripe Elements Payment Element state (replaces the old
  // window.location.href redirect to Stripe-hosted Checkout). clientSecret gates
  // rendering the Payment Element inline on the 'review' pricing step;
  // pendingSubscriptionId is the Subscription created by /checkout/session, used to
  // call the finalize endpoint once confirmPayment() resolves.
  const [clientSecret, setClientSecret] = useState(null);
  const [pendingSubscriptionId, setPendingSubscriptionId] = useState(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  // Stripe's automatic_tax needs a Customer address on file (see backend
  // ensure_stripe_customer_with_branding); once saved it's returned by GET
  // billing-address and this form is skipped on future checkouts.
  const [savedBillingAddress, setSavedBillingAddress] = useState(null);
  // Toggles the read-only summary vs. the editable Stripe AddressElement on the Payment
  // step -- defaults to editable until a saved address is loaded (see
  // fetchAllBillingData), so a first-time checkout goes straight to the form instead of
  // flashing an empty summary first.
  const [editingBillingAddress, setEditingBillingAddress] = useState(false);
  // Tracks the mounted AddressElement's own completeness (its 'change' event) -- Stripe
  // validates per-country field requirements itself (e.g. state is required for US/CA,
  // not for others), so this is more correct than re-deriving completeness by hand.
  const [addressElementComplete, setAddressElementComplete] = useState(false);
  // Whether the Payment step's billing-address UI needs a completed AddressElement before
  // checkout can proceed -- false once an address is already on file and the user hasn't
  // opened the editor. Declared here (not lower, near the other `selected*` derived
  // values) because the AddressElement mount effect below reads it.
  const needsAddressInput = !savedBillingAddress || editingBillingAddress;
  const stripeElementsRef = useRef(null);
  const paymentElementContainerRef = useRef(null);
  const addressElementRef = useRef(null);
  const addressElementContainerRef = useRef(null);

  const fetchAllBillingData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch billing status
      const statusRes = await api.get('/settings/billing');
      setBillingStatus(statusRes.data);

      // Fetch payment history
      const paymentsRes = await fetch(`${API_URL}/api/billing/stripe/payments?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (paymentsRes.ok) {
        const paymentsData = await paymentsRes.json();
        setPaymentHistory(paymentsData.payments || []);
      }

      // Fetch pricing tiers from public endpoint (accessible to all users)
      const pricingRes = await fetch(`${API_URL}/api/settings/pricing-tiers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (pricingRes.ok) {
        const data = await pricingRes.json();
        if (data.tiers && data.tiers.length > 0) {
          // Map icons based on tier position/name
          const iconMap = {
            0: { icon: Zap, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
            1: { icon: TrendingUp, color: 'text-green-500', bgColor: 'bg-green-500/10', popular: true },
            2: { icon: Shield, color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
            3: { icon: Shield, color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
            4: { icon: Star, color: 'text-rose-500', bgColor: 'bg-rose-500/10' },
          };
          
          const formattedTiers = data.tiers.map((tier, idx) => ({
            id: tier.name.toLowerCase().replace(/\s+/g, '_'),
            dbId: tier.id,
            name: tier.name,
            description: PLAN_DESCRIPTIONS[tier.name] || tier.description,
            monthlyPrice: tier.price_per_user,
            annualPrice: tier.price_per_user == null ? null : Math.round(tier.price_per_user * 0.9 * 100) / 100, // 10% annual discount
            content_team_limit: tier.content_team_limit,
            employee_limit: tier.employee_limit,
            document_limit: tier.document_limit,
            max_chunks_per_document: tier.max_chunks_per_document,
            topics_limit: tier.topics_limit,
            monthly_token_limit: tier.monthly_token_limit,
            conversation_monthly_token_limit: tier.conversation_monthly_token_limit,
            kb_monthly_token_limit: tier.kb_monthly_token_limit,
            monthly_cost_limit_usd: tier.monthly_cost_limit_usd,
            tokens_per_seat_month: tier.tokens_per_seat_month,
            user_daily_capacity_pct: tier.user_daily_capacity_pct,
            user_weekly_capacity_pct: tier.user_weekly_capacity_pct,
            user_monthly_capacity_pct: tier.user_monthly_capacity_pct,
            integrations_limit: tier.integrations_limit,
            monthly_export_limit: tier.monthly_export_limit,
            scheduled_reports_limit: tier.scheduled_reports_limit,
            notification_limit: tier.notification_limit,
            features: (tier.features || []).map(f => ({ text: f, included: true })),
            ...iconMap[idx] || iconMap[0],
          }));
          setPricingTiers(formattedTiers);
        }
      }

      // Fetch available credits
      const creditsRes = await fetch(`${API_URL}/api/billing/stripe/credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (creditsRes.ok) {
        const creditsData = await creditsRes.json();
        setCredits(creditsData);
      }

      // Fetch available payment methods
      const paymentMethodsRes = await fetch(`${API_URL}/api/billing/stripe/payment-methods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (paymentMethodsRes.ok) {
        const pmData = await paymentMethodsRes.json();
        setAvailablePaymentMethods(pmData.payment_methods || []);
        setRenewalPaymentMethod(pmData.saved_payment_method || null);
        setRenewalReady(Boolean(pmData.renewal_ready));
        if (pmData.default) {
          setPaymentMethod(pmData.default);
        }
      }

      // Prefill a previously-saved billing address, if any (see billing_address on
      // CheckoutRequest -- avoids re-asking once it's on file).
      const billingAddressRes = await fetch(`${API_URL}/api/billing/stripe/billing-address`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (billingAddressRes.ok) {
        const addressData = await billingAddressRes.json();
        if (addressData.billing_address) {
          setSavedBillingAddress(addressData.billing_address);
        }
      }
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch all billing data
  useEffect(() => {
    fetchAllBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only load
  }, []);

  // Shared "payment confirmed" sequence -- used by both the legacy Checkout-Session
  // status poll below AND the new embedded Payment Element flow (handleConfirmPayment),
  // so there is exactly one success code path regardless of how the client_secret was
  // confirmed.
  const onSubscriptionPaymentSuccess = useCallback(async () => {
    toast.success('Payment successful! Your subscription is now active.');
    if (refreshTenant) {
      await refreshTenant();
    }
    await fetchAllBillingData();
    setShowPricingDialog(false);
    setClientSecret(null);
    setPendingSubscriptionId(null);
    setPaymentError('');
  }, [refreshTenant, fetchAllBillingData]);

  // Check for payment status from URL
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    const clearCheckoutStatusParams = () => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('session_id');
      nextParams.delete('status');
      setSearchParams(nextParams, { replace: true });
    };

    const checkPaymentStatus = async (sid) => {
      try {
        const response = await fetch(`${API_URL}/api/billing/stripe/checkout/status/${sid}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();

        if (data.payment_status === 'paid' || data.status === 'complete') {
          await onSubscriptionPaymentSuccess();
          clearCheckoutStatusParams();
        } else {
          toast.info(`Payment status: ${data.status}`);
        }
      } catch (_err) {
        console.error('Failed to check payment status:', _err);
      }
    };

    if (sessionId && status === 'success') {
      if (processedCheckoutSessionRef.current === sessionId) {
        return;
      }
      processedCheckoutSessionRef.current = sessionId;
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled');
      clearCheckoutStatusParams();
    }
  }, [searchParams, setSearchParams, token, refreshTenant, fetchAllBillingData, onSubscriptionPaymentSuccess]);

  const fetchCheckoutQuote = useCallback(async () => {
    const tierData = pricingTiers.find(t => t.id === selectedTier);
    if (!tierData || !tierData.monthlyPrice) {
      setCheckoutQuote(null);
      return null;
    }

    const packageId = `${selectedTier}_${billingPeriod === 'annual' ? 'annual' : 'monthly'}`;
    const seatCount = Math.max(Number(checkoutSeatCount) || 1, 1);
    setQuoteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/billing/stripe/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: packageId,
          billing_period: billingPeriod,
          apply_credits: applyCredits && credits.balance > 0,
          seat_count: seatCount,
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setCheckoutQuote(null);
        toast.error(data.detail || 'Failed to calculate checkout total');
        return null;
      }
      setCheckoutQuote(data);
      return data;
    } catch (err) {
      console.error('Quote error:', err);
      setCheckoutQuote(null);
      toast.error('Failed to calculate checkout total');
      return null;
    } finally {
      setQuoteLoading(false);
    }
  }, [pricingTiers, selectedTier, billingPeriod, applyCredits, credits.balance, checkoutSeatCount, token]);

  useEffect(() => {
    if (showPricingDialog) {
      fetchCheckoutQuote();
    }
  }, [showPricingDialog, fetchCheckoutQuote]);

  const fetchEnterpriseQuote = useCallback(async (requestedEmployeeCount = null) => {
    // EIP-249: size Enterprise from the real headcount (all active users) that seats must
    // cover, not the employee-only-role count which is 0 for all-admin tenants.
    const currentHeadcount = billingStatus?.usage?.active_users
      ?? ((billingStatus?.usage?.employees ?? 0) + (billingStatus?.usage?.content_team ?? 0));
    const employeeCount = Number(requestedEmployeeCount || checkoutSeatCount || enterpriseEmployeeCount || currentHeadcount || 1);
    if (!Number.isFinite(employeeCount) || employeeCount < 1) {
      toast.error('Enter an employee count to size Enterprise');
      return null;
    }
    setEnterpriseQuoteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/billing/stripe/enterprise/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_count: employeeCount,
          billing_period: billingPeriod,
        })
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Failed to request Enterprise quote');
        return null;
      }
      setEnterpriseQuote(data);
      return data;
    } catch (err) {
      console.error('Enterprise quote error:', err);
      toast.error('Failed to request Enterprise quote');
      return null;
    } finally {
      setEnterpriseQuoteLoading(false);
    }
  }, [billingStatus?.usage?.active_users, billingStatus?.usage?.employees, billingStatus?.usage?.content_team, billingPeriod, checkoutSeatCount, enterpriseEmployeeCount, token]);

  const handleCheckout = async () => {
    const tierData = pricingTiers.find(t => t.id === selectedTier);
    if (!tierData) {
      toast.error('Select a plan to continue');
      return;
    }

    setCheckoutLoading(true);
    const packageId = `${selectedTier}_${billingPeriod === 'annual' ? 'annual' : 'monthly'}`;
    // EIP-273: Enterprise never reaches this function — its Review-step CTA is
    // "Contact Sales", not "Continue to Checkout" — so only self-serve tiers checkout here.
    const quote = checkoutQuote || await fetchCheckoutQuote();
    if (!quote?.can_checkout) {
      toast.error(quote?.blockers?.[0]?.message || 'Selected plan cannot support current usage');
      setCheckoutLoading(false);
      return;
    }

    // Scheduled at-renewal changes (downgrade/seat-decrease) never reach the Payment step
    // and don't need an address at all -- see disabled-condition comments below.
    let billingAddressPayload = null;
    if (!selectedIsDowngrade && !selectedIsSeatDecrease && needsAddressInput) {
      if (!addressElementRef.current) {
        toast.error('Enter a billing address to continue — Stripe needs it to calculate tax.');
        setCheckoutLoading(false);
        return;
      }
      const { complete, value } = await addressElementRef.current.getValue();
      if (!complete) {
        toast.error('Enter a complete billing address to continue.');
        setCheckoutLoading(false);
        return;
      }
      billingAddressPayload = {
        name: value.name,
        line1: value.address.line1,
        line2: value.address.line2 || '',
        city: value.address.city,
        state: value.address.state,
        postal_code: value.address.postal_code,
        country: value.address.country,
      };
    }

    try {
      const response = await fetch(`${API_URL}/api/billing/stripe/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
          apply_credits: applyCredits && credits.balance > 0,
          payment_method: paymentMethod,
          seat_count: Math.max(Number(checkoutSeatCount) || 1, 1),
          billing_address: billingAddressPayload,
        })
      });
      
      const data = await response.json();

      if (response.ok && data.client_secret) {
        // Show credit application info if credits were applied
        if (data.credits_applied > 0) {
          toast.success(`$${data.credits_applied.toFixed(2)} referral balance applied to your order!`);
        }
        // Embedded Payment Element instead of a redirect to Stripe-hosted Checkout --
        // setting clientSecret mounts the Payment Element inline on the 'review' step
        // (see the mount effect and the review-step render below).
        setPendingSubscriptionId(data.subscription_id);
        setClientSecret(data.client_secret);
        if (billingAddressPayload) {
          setSavedBillingAddress(billingAddressPayload);
          setEditingBillingAddress(false);
        }
      } else if (response.ok && data.status === 'upgraded') {
        toast.success('Plan updated. Stripe will invoice any prorated difference.');
        setShowPricingDialog(false);
        if (refreshTenant) {
          await refreshTenant();
        }
        fetchAllBillingData();
      } else if (response.ok && data.status === 'scheduled') {
        toast.success(data.message || 'Change scheduled for your next renewal.');
        setShowPricingDialog(false);
        if (refreshTenant) {
          await refreshTenant();
        }
        fetchAllBillingData();
      } else {
        const message = typeof data.detail === 'object'
          ? data.detail.message || data.detail.blockers?.[0]?.message
          : data.detail;
        toast.error(message || 'Failed to start checkout');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Mounts the Payment Element as soon as a client_secret is available on the
  // 'review' step. Themed via the Appearance API (buildStripeAppearance) so it
  // matches this page's own light/dark palette instead of Stripe's default look.
  useEffect(() => {
    if (!clientSecret || pricingStep !== 'payment') return;
    let cancelled = false;

    (async () => {
      const stripe = await getStripe();
      if (!stripe || cancelled || !paymentElementContainerRef.current) return;
      const elements = stripe.elements({ clientSecret, appearance: buildStripeAppearance() });
      elements.create('payment').mount(paymentElementContainerRef.current);
      stripeElementsRef.current = { stripe, elements };
    })();

    return () => {
      cancelled = true;
      stripeElementsRef.current = null;
    };
  }, [clientSecret, pricingStep]);

  // Mounts Stripe's AddressElement on the Payment step whenever an address still needs
  // collecting -- unlike the Payment Element above, this needs no client_secret (it's a
  // form component, not tied to a PaymentIntent), so it mounts as soon as the step shows
  // and doesn't wait on a checkout/session round-trip. Deliberately does NOT gate on
  // selectedIsEnterprise/selectedIsDowngrade/selectedIsSeatDecrease -- those aren't computed
  // until later in this component and this effect must run before the `if (loading) return`
  // guard below it (Rules of Hooks: hooks can't follow a conditional early return). The
  // JSX only renders addressElementContainerRef's container when none of those apply, so
  // the ref stays null and this effect harmlessly no-ops for those cases instead.
  useEffect(() => {
    if (pricingStep !== 'payment' || !needsAddressInput) return;
    let cancelled = false;
    setAddressElementComplete(false);

    (async () => {
      const stripe = await getStripe();
      if (!stripe || cancelled || !addressElementContainerRef.current) return;
      const elements = stripe.elements({ appearance: buildStripeAppearance() });
      const addressElement = elements.create('address', {
        mode: 'billing',
        defaultValues: {
          name: savedBillingAddress?.name || user?.name || '',
          address: {
            line1: savedBillingAddress?.line1 || '',
            line2: savedBillingAddress?.line2 || '',
            city: savedBillingAddress?.city || '',
            state: savedBillingAddress?.state || '',
            postal_code: savedBillingAddress?.postal_code || '',
            country: savedBillingAddress?.country || 'US',
          },
        },
        fields: { phone: 'never' },
      });
      addressElement.mount(addressElementContainerRef.current);
      addressElement.on('change', (event) => setAddressElementComplete(event.complete));
      addressElementRef.current = addressElement;
    })();

    return () => {
      cancelled = true;
      if (addressElementRef.current) {
        addressElementRef.current.unmount();
        addressElementRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only remount on step/edit-mode change; savedBillingAddress/user feed defaultValues at mount time only
  }, [pricingStep, needsAddressInput]);

  const handleConfirmPayment = async () => {
    if (!stripeElementsRef.current) return;
    const { stripe, elements } = stripeElementsRef.current;
    setPaymentSubmitting(true);
    setPaymentError('');
    try {
      const confirmResult = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (confirmResult.error) {
        setPaymentError(confirmResult.error.message || 'Payment failed. Please try again.');
        return;
      }
      // Confirmed inline with no redirect needed (the common case for a card with no
      // extra authentication). Re-verify against Stripe server-side before activating
      // the subscription -- never trust the browser's report alone.
      const response = await fetch(
        `${API_URL}/api/billing/stripe/checkout/finalize-subscription/${pendingSubscriptionId}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok && (data.status === 'complete')) {
        await onSubscriptionPaymentSuccess();
      } else {
        setPaymentError('Payment is still processing. We will activate your plan automatically once it completes.');
      }
    } catch (err) {
      console.error('Payment confirmation error:', err);
      setPaymentError('Something went wrong confirming your payment. Please try again.');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/billing/stripe/subscription/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchAllBillingData();
        if (refreshTenant) {
          await refreshTenant();
        }
      } else {
        toast.error(data.detail || 'Failed to cancel subscription');
      }
    } catch {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleReactivateSubscription = async () => {
    if (!confirm('Are you sure you want to reactivate your subscription? Billing will resume at the end of your current period.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/billing/stripe/subscription/reactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Subscription reactivated successfully');
        fetchAllBillingData();
        if (refreshTenant) {
          await refreshTenant();
        }
      } else {
        toast.error(data.detail || 'Failed to reactivate subscription');
      }
    } catch {
      toast.error('Failed to reactivate subscription');
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await fetch(`${API_URL}/api/billing/stripe/portal/session`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (response.ok && data.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error(data.detail || 'Failed to open billing portal');
      }
    } catch {
      toast.error('Failed to open billing portal');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return formatCellValue(dateStr);
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };
  const formatMoney = (amount) => formatCurrency(Number(amount || 0));

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        kpis={4}
        sections={2}
        sectionClassName="h-80"
        data-testid="billing-loading"
      />
    );
  }

  const isAdmin = user?.platform_role === 'admin' || user?.roles?.includes('admin');

  if (!isAdmin) {
    return (
      <PageShell maxWidth="7xl">
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertTriangle className="h-8 w-8 text-warning" />
            <div>
              <h3 className="font-semibold">Access Denied</h3>
              <p className="text-muted-foreground">Only administrators can view billing and subscription settings.</p>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  const subscriptionStatus = billingStatus?.subscription_status || tenant?.subscription_status || 'trial';
  // build_subscription_view's provenance-aware label (EIP-332) — falls back to the raw
  // status only for a response shape from before this shipped. Using this instead of
  // the raw column is what keeps a paying tenant whose Stripe subscription reports
  // "trialing" (trial_end set to the paid-through date on the seat-sync path) from
  // being read as an actual trial here.
  const displayStatus = billingStatus?.subscription?.display_status || subscriptionStatus;
  const subscriptionState = billingStatus?.subscription?.state;
  const isTrial = displayStatus === 'trial' || displayStatus === 'trial_expired';
  const isActive = subscriptionStatus === 'active';
  // Use tenant value for consistency with banner (both from same login response)
  // Only fall back to billingStatus if tenant doesn't have the value
  // No hardcoded fallback - show 0 if data unavailable
  const trialDaysRemaining = tenant?.trial_days_remaining ?? billingStatus?.trial?.days_remaining ?? 0;
  const trialExpired = (billingStatus?.trial?.expired) || trialDaysRemaining <= 0;
  const subscriptionValid = billingStatus?.subscription?.is_valid;
  // For trial users, show "Trial" as the plan, not "Starter"
  const currentTier = isTrial ? 'Trial' : (billingStatus?.subscription?.tier || billingStatus?.pricing?.tier_name || tenant?.pricing_tier || 'Starter');
  const currentTierId = normalizeTierId(currentTier);
  // Enterprise is a negotiated contract, not a self-serve tier — downgrading FROM it
  // stays contact-sales (the self-serve downgrade path only covers Starter <-> Growth).
  const currentIsEnterprise = currentTierId === 'enterprise';
  const currentBillingPeriod = billingStatus?.subscription?.billing_period || null;
  const planLimits = billingStatus?.plan_limits || {};
  const planConfiguration = billingStatus?.configuration || {};
  const missingRequiredLimits = new Set(planConfiguration.missing_required_limits || []);
  const requiresEnterpriseConfiguration = Boolean(planConfiguration.is_enterprise && !planConfiguration.can_activate);
  const selectedTierData = pricingTiers.find(t => t.id === selectedTier);
  const selectedTierIndex = pricingTiers.findIndex(t => t.id === selectedTier);
  const currentTierIndex = pricingTiers.findIndex(t => t.id === currentTierId);
  // A pricing_tier provisioned by superadmin (e.g. a paid tenant born pending_payment)
  // is not the same thing as a plan the tenant has actually purchased — treating it as
  // "current" makes the tier permanently unsellable (EIP-328). Payment history entries
  // use the same status vocabulary as the activation gate (superadmin.py's
  // tenant_has_paid_payment: paid/completed/succeeded).
  const hasPurchasedPlan = isActive
    || Boolean(billingStatus?.subscription?.has_stripe_subscription)
    || paymentHistory.some((p) => ['paid', 'completed', 'succeeded'].includes(p.status));
  const selectedIsCurrentTier = !isTrial && hasPurchasedPlan && selectedTier === currentTierId;
  const selectedIsCurrentPlan = selectedIsCurrentTier && (!currentBillingPeriod || billingPeriod === currentBillingPeriod);
  const selectedIsDowngrade = !isTrial && currentTierIndex !== -1 && selectedTierIndex !== -1 && selectedTierIndex < currentTierIndex;
  const selectedIsUpgrade = !isTrial && currentTierIndex !== -1 && selectedTierIndex !== -1 && selectedTierIndex > currentTierIndex;
  const selectedIsBillingChange = selectedIsCurrentTier && currentBillingPeriod && billingPeriod !== currentBillingPeriod;
  const selectedIsEnterprise = selectedTier === 'enterprise';
  const selectedEnterpriseConfigured = selectedIsEnterprise && selectedIsCurrentTier && !requiresEnterpriseConfiguration;
  const usageStats = billingStatus?.usage || {};
  // EIP-249: seats must cover ALL active users (employees + content team), matching the
  // backend billable-seat floor (get_billable_seat_counts -> total_users). usage.employees
  // alone is the employee-only-role count, which reads 0 for tenants that are all admins,
  // so use active_users and fall back to the shared seat-pool sum.
  const currentEmployeeCount = Number(
    usageStats.active_users ?? ((usageStats.employees ?? 0) + (usageStats.content_team ?? 0))
  );
  const requestedSeatCount = Math.max(Number(checkoutSeatCount) || 0, 0);
  const minimumSeatCount = Math.max(currentEmployeeCount, 1);
  const currentPaidSeatCount = Number(checkoutQuote?.current_paid_seats ?? paymentHistory?.[0]?.user_count ?? currentEmployeeCount ?? 1);
  // Seats the tenant has actually paid for (0 while on trial — nothing purchased yet).
  const currentPurchasedSeats = isTrial ? 0 : currentPaidSeatCount;
  // Delta vs. current purchased seats, surfaced as the "additional seats" readout (EIP-249).
  const additionalSeats = requestedSeatCount - currentPurchasedSeats;
  const selectedSeatCountChanged = requestedSeatCount > 0 && requestedSeatCount !== currentPaidSeatCount;
  const selectedIsSeatIncrease = selectedIsCurrentPlan && requestedSeatCount > currentPaidSeatCount;
  const selectedIsSeatDecrease = selectedIsCurrentPlan && requestedSeatCount < currentPaidSeatCount;

  const selectedTierEmployeeLimit = selectedTierData?.employee_limit;
  const seatCountBlocker = requestedSeatCount < minimumSeatCount
    ? requestedSeatCount <= 0
      ? `Enter at least ${minimumSeatCount.toLocaleString()} ${minimumSeatCount === 1 ? 'seat' : 'seats'} to continue.`
      : `Purchase at least ${minimumSeatCount.toLocaleString()} ${minimumSeatCount === 1 ? 'seat' : 'seats'} to cover current employees.`
    : selectedTierEmployeeLimit && requestedSeatCount > selectedTierEmployeeLimit
      ? `${selectedTierData?.name} supports up to ${selectedTierEmployeeLimit.toLocaleString()} employees.`
      : null;
  const pricingSteps = [
    { key: 'plan', label: 'Plan', description: 'Choose the subscription tier.' },
    { key: 'seats', label: 'Seats', description: 'Enter the total number of seats to purchase.' },
    { key: 'billing', label: 'Billing', description: 'Choose renewal cadence and payment method.' },
    { key: 'review', label: 'Review', description: 'Confirm pricing, limits, and checkout.' },
    { key: 'payment', label: 'Payment', description: 'Enter your billing address and payment details.' },
  ];
  const pricingStepIndex = pricingSteps.findIndex((step) => step.key === pricingStep);
  const canAdvancePricingStep = pricingStep !== 'seats' || !seatCountBlocker;
  // EIP-251: self-serve downgrades (Starter <-> Growth) are allowed when the server
  // quote allows them — the server enforces the active-users-vs-target-seats floor
  // (populating checkoutQuote.blockers / can_checkout=false), so the client trusts the
  // quote. The client still hard-blocks (a) the no-op "already on this plan" case and
  // (b) downgrades FROM Enterprise, which are not self-serve.
  const clientCheckoutBlocker = selectedIsCurrentPlan && !selectedSeatCountChanged
    ? `You are already on ${currentTier}${currentBillingPeriod ? ` ${currentBillingPeriod}` : ''}. Select a different plan or billing period to continue.`
    : selectedIsDowngrade && currentIsEnterprise
      ? 'Downgrading from Enterprise isn’t self-serve. Contact support to change your plan.'
      : null;
  const billingStatusForDisplay = requiresEnterpriseConfiguration ? 'configuration_required' : displayStatus;
  const isLimitConfigured = (key) => !requiresEnterpriseConfiguration || !missingRequiredLimits.has(key);
  const hasConfiguredLimitValue = (key) => {
    const value = planLimits[key];
    if (!isLimitConfigured(key)) return false;
    if (value === null || value === undefined) {
      return Boolean(planConfiguration.allows_unlimited);
    }
    return true;
  };
  const tokensPerSeat = planLimits.tokens_per_seat_month;
  const conversationTokensPerSeat = tokensPerSeat;
  const conversationMonthlyTokens = planLimits.conversation_monthly_token_limit
    ?? (conversationTokensPerSeat ? conversationTokensPerSeat * Math.max(currentEmployeeCount, 1) : null);
  const conversationMonthlyTokensUsed = usageStats.monthly_conversation_tokens ?? 0;
  const conversationTokensPerSeatUsed = currentEmployeeCount > 0
    ? Math.round(conversationMonthlyTokensUsed / currentEmployeeCount)
    : conversationMonthlyTokensUsed;
  const conversationMonthlyTokensPercent = conversationMonthlyTokens
    ? (conversationMonthlyTokensUsed / conversationMonthlyTokens) * 100
    : 0;
  const conversationTokensPerSeatPercent = conversationTokensPerSeat
    ? (conversationTokensPerSeatUsed / conversationTokensPerSeat) * 100
    : 0;
  const knowledgeBaseMonthlyTokens = planLimits.kb_monthly_token_limit;
  const conversationTokensPerSeatLabel = conversationTokensPerSeat
    ? `Average usage across ${Math.max(currentEmployeeCount, 1).toLocaleString()} employee ${Math.max(currentEmployeeCount, 1) === 1 ? 'seat' : 'seats'}`
    : null;
  // EIP-224: employees and content team share one seat pool. Combined capacity
  // is the sum of both limits (either unlimited => unlimited); usage is all users.
  const seatLimit = (planLimits.employee_limit == null || planLimits.content_team_limit == null)
    ? null
    : planLimits.employee_limit + planLimits.content_team_limit;
  const seatsUsed = (usageStats.employees ?? 0) + (usageStats.content_team ?? 0);
  const entitlementSections = [
    {
      key: 'knowledge-base',
      title: 'Knowledge Base',
      icon: BookOpen,
      meters: [
        {
          key: 'knowledge_base_monthly_token_limit',
          title: 'Eva Insights Credits',
          used: usageStats.monthly_kb_tokens ?? 0,
          limit: knowledgeBaseMonthlyTokens,
          icon: Zap,
          formatter: formatTokens,
          caption: 'Shared by content team workflows',
          displayType: 'metered',
          configuredKey: 'monthly_token_limit',
        },
	        {
	          key: 'document_limit',
	          title: 'Documents',
	          used: usageStats.documents ?? 0,
	          icon: FileText,
	          caption: 'Knowledge base capacity',
	          displayType: 'capacity',
	        },
        {
          key: 'max_chunks_per_document',
          title: 'Pages / Document',
          icon: FileText,
          caption: 'Applied during upload',
          displayType: 'rule',
        },
	        {
	          key: 'topics_limit',
	          title: 'Topics',
	          used: usageStats.topics ?? 0,
	          icon: Tags,
	          caption: 'Topic capacity',
	          displayType: 'capacity',
	        },
        {
          key: 'content_gap_limit',
          title: 'Content Gaps',
          used: usageStats.content_gaps ?? 0,
          icon: FileQuestion,
          caption: 'Content gap workspace capacity',
          displayType: 'capacity',
        },
      ],
    },
    {
      key: 'people',
      title: 'People',
      icon: Users,
      meters: [
        {
          key: 'seats',
          configuredKey: 'employee_limit',
          title: 'Seats',
          used: seatsUsed,
          limit: seatLimit,
          icon: Users,
          caption: 'Employees and content team share one seat pool',
          displayType: 'capacity',
        },
      ],
    },
    {
      key: 'conversations',
      title: 'Conversations',
      icon: MessageSquare,
      meters: [
        {
          key: 'conversation_monthly_token_limit',
          title: 'Total Eva People Credits',
          used: conversationMonthlyTokensUsed,
          limit: conversationMonthlyTokens,
          icon: Zap,
          formatter: formatTokens,
          caption: 'Shared monthly pool for Eva People',
          percentUsed: conversationMonthlyTokensPercent,
          displayType: 'metered',
          configuredKey: planLimits.conversation_monthly_token_limit ? 'conversation_monthly_token_limit' : 'tokens_per_seat_month',
          testId: 'conversation-total-token-meter',
        },
        ...(isTrial ? [] : [{
          key: 'conversation_tokens_per_seat_month',
          title: 'Eva People Credits / Seat',
          used: conversationTokensPerSeatUsed,
          limit: conversationTokensPerSeat,
          icon: Zap,
          formatter: formatTokens,
          caption: conversationTokensPerSeatLabel || 'Metered monthly per employee seat in Eva People',
          percentUsed: conversationTokensPerSeatPercent,
          displayType: 'metered',
          configuredKey: 'tokens_per_seat_month',
          testId: 'conversation-token-seat-meter',
        }]),
      ],
    },
    {
      key: 'engagement',
      title: 'Engagement',
      icon: Trophy,
      meters: [
        {
          key: 'monthly_export_limit',
          title: 'Monthly Exports',
          used: usageStats.monthly_exports ?? 0,
          icon: Download,
          caption: 'Resets each billing month',
          displayType: 'metered',
        },
        {
          key: 'scheduled_reports_limit',
          title: 'Scheduled Reports',
          used: usageStats.scheduled_reports ?? 0,
          icon: Bell,
          caption: 'Active report schedules',
          displayType: 'capacity',
        },
        {
          key: 'notification_limit',
          title: 'Notifications',
          used: usageStats.notifications_sent_30d ?? 0,
          icon: Bell,
          caption: 'Rolling 30-day send capacity',
          displayType: 'capacity',
        },
      ],
    },
    {
      key: 'connectors',
      title: 'Connectors',
      icon: Link2,
      meters: [
        {
          key: 'integrations_limit',
          title: 'Integrations',
          used: usageStats.integrations ?? 0,
          icon: ExternalLink,
          caption: 'Connected system capacity',
          displayType: 'capacity',
        },
      ],
    },
  ];
  const toggleEntitlementSection = (sectionKey) => {
    setCollapsedEntitlementSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  };
  const getMeterLimit = (meter) => meter.limit ?? planLimits[meter.key];
  const isMeterConfigured = (meter) => hasConfiguredLimitValue(meter.configuredKey || meter.key);
  const getMeterState = (meter) => {
    if (!isMeterConfigured(meter)) {
      return { key: 'not_configured', label: 'Not configured', percent: 0 };
    }
    const limit = getMeterLimit(meter);
    const isUnlimited = limit === null || limit === undefined || limit === -1;
    const rawPercent = meter.percentUsed ?? (isUnlimited || Number(limit) <= 0 ? 0 : (Number(meter.used || 0) / Number(limit)) * 100);
    const percent = Math.max(0, Number(rawPercent || 0));
    if (isUnlimited) return { key: 'unlimited', label: 'Unlimited', percent: 0 };
    if (percent >= 100) return { key: 'exceeded', label: 'At Limit', percent };
    if (percent >= 90) return { key: 'critical', label: 'Critical', percent };
    if (percent >= 80) return { key: 'warning', label: 'Near Limit', percent };
    return { key: 'normal', label: meter.displayType === 'capacity' ? 'Capacity' : 'Healthy', percent };
  };
  const getMeterBadgeClass = (stateKey, displayType) => {
    if (stateKey === 'not_configured') return 'border-amber-300 bg-amber-500/10 text-amber-700 dark:text-amber-300';
    if (displayType === 'rule') return 'border-primary/30 bg-primary/10 text-primary';
    if (stateKey === 'unlimited') return 'border-purple-200 bg-purple-500/10 text-purple-600';
    if (stateKey === 'exceeded') return 'border-destructive/30 bg-destructive/10 text-destructive';
    if (stateKey === 'critical' || stateKey === 'warning') return 'border-warning/30 bg-warning/10 text-warning';
    if (displayType === 'capacity') return 'border-blue-200 bg-blue-500/10 text-blue-600';
    return 'border-emerald-200 bg-emerald-500/10 text-emerald-600';
  };
  const getMeterBadgeLabel = (meter, state) => {
    if (state.key === 'not_configured') return 'Not configured';
    if (meter.displayType === 'rule') return 'Plan rule';
    if (state.key === 'unlimited') return 'Unlimited';
    if (state.key === 'exceeded') return 'At Limit';
    if (state.key === 'critical' || state.key === 'warning') return 'Near Limit';
    if (meter.displayType === 'capacity') return 'Capacity';
    return 'Healthy';
  };
  const getMeterRemainingLabel = (meter, limit, state) => {
    const formatter = meter.formatter || formatCompactNumber;
    if (state.key === 'not_configured') return 'Configure limit';
    if (meter.displayType === 'rule') return 'Action rule';
    if (state.key === 'unlimited') return 'No fixed cap';
    const remaining = Math.max(Number(limit || 0) - Number(meter.used || 0), 0);
    const remainingPercent = formatRemainingPercent(remaining, limit);
    return remainingPercent
      ? `${formatter(remaining)} remaining (${remainingPercent})`
      : `${formatter(remaining)} remaining`;
  };
  const getMeterUsageLabel = (meter, limit, state) => {
    const formatter = meter.formatter || formatCompactNumber;
    if (meter.displayType === 'rule') return '—';
    if (state.key === 'not_configured') return formatter(meter.used || 0);
    if (state.key === 'unlimited') return formatter(meter.used || 0);
    return `${formatter(meter.used || 0)} of ${formatter(limit)}`;
  };
  const selectedIncludedLimits = selectedEnterpriseConfigured
    ? planLimits
    : selectedIsEnterprise
      ? enterpriseQuote?.limits || {}
    : selectedTierData || {};
  const getIncludedLimitValue = (key) => {
    if (key === 'conversation_tokens_per_seat_month') {
      return selectedIncludedLimits.tokens_per_seat_month;
    }
    if (key === 'knowledge_base_monthly_token_limit') {
      return selectedIncludedLimits.kb_monthly_token_limit;
    }
    if (key === 'seats') {
      // EIP-224: one seat pool = employee_limit + content_team_limit (either
      // unlimited => unlimited, surfaced as null for formatIncludedLimit).
      const e = selectedIncludedLimits.employee_limit;
      const c = selectedIncludedLimits.content_team_limit;
      if (e == null || e === -1 || c == null || c === -1) return null;
      return e + c;
    }
    return selectedIncludedLimits[key];
  };
  const formatIncludedLimit = (key, formatter = formatCompactNumber) => {
    const value = getIncludedLimitValue(key);
    if (selectedIsEnterprise && (value === null || value === undefined || value === -1)) {
      return 'Size pending';
    }
    if (value === -1 || value === null || value === undefined) {
      return 'Unlimited';
    }
    return formatter(value);
  };
  const includedEntitlementSections = [
    {
      key: 'knowledge-base',
      title: 'Knowledge Base',
      icon: BookOpen,
      description: 'Content and topic limits for the knowledge base.',
      items: [
        { key: 'knowledge_base_monthly_token_limit', label: 'Eva Insights Credits', icon: Zap, color: 'text-rose-500', formatter: formatTokens },
	        { key: 'document_limit', label: 'Documents', icon: FileText, color: 'text-amber-500' },
	        { key: 'max_chunks_per_document', label: 'Pages / Document', icon: FileText, color: 'text-sky-500' },
	        { key: 'topics_limit', label: 'Topics', icon: Tags, color: 'text-teal-500' },
        { key: 'content_gap_limit', label: 'Content Gaps', icon: FileQuestion, color: 'text-red-500' },
      ],
    },
    {
      key: 'people',
      title: 'People',
      icon: Users,
      description: 'Seat capacity shared by employees and content team.',
      items: [
        { key: 'seats', label: 'Seats', icon: Users, color: 'text-blue-500' },
      ],
    },
    {
      key: 'conversations',
      title: 'Conversations',
      icon: MessageSquare,
      description: 'Per-seat monthly credits for Eva People.',
      items: isTrial
        ? [{ key: 'conversation_monthly_token_limit', label: 'Total Eva People Credits', icon: Users, color: 'text-emerald-500', formatter: formatTokens }]
        : [{ key: 'conversation_tokens_per_seat_month', label: 'Eva People Credits / Seat', icon: Users, color: 'text-emerald-500', formatter: formatTokens }],
    },
    {
      key: 'engagement',
      title: 'Engagement',
      icon: Trophy,
      description: 'Exports, scheduled reports, and notification-driven workflows.',
      items: [
        { key: 'monthly_export_limit', label: 'Monthly Exports', icon: Download, color: 'text-indigo-500' },
        { key: 'scheduled_reports_limit', label: 'Scheduled Reports', icon: Bell, color: 'text-pink-500' },
        { key: 'notification_limit', label: 'Notifications', icon: Bell, color: 'text-sky-500' },
      ],
    },
    {
      key: 'connectors',
      title: 'Connectors',
      icon: Link2,
      description: 'Connected systems available to this plan.',
      items: [
        { key: 'integrations_limit', label: 'Integrations', icon: ExternalLink, color: 'text-cyan-500' },
      ],
    },
  ];
  const shouldShowPaymentMethodWarning = subscriptionValid
    && billingStatus?.subscription?.has_stripe_subscription
    && billingStatus?.subscription?.auto_renew !== false
    && !renewalReady;
  const openPricingDialog = (initialTier = null) => {
    const hasInitialTier = initialTier && pricingTiers.some(t => t.id === initialTier);
    const defaultTier = !isTrial && pricingTiers.some(t => t.id === currentTierId)
      ? currentTierId
      : 'starter';
    // EIP-249: default to a seat count that covers current headcount and any seats already
    // purchased, so the dialog opens without an immediate "cover current employees" blocker.
    const defaultSeatCount = Math.max(
      currentEmployeeCount,
      isTrial ? 0 : (Number(currentPaidSeatCount) || 0),
      1
    );

    setSelectedTier(hasInitialTier ? initialTier : defaultTier);
    setBillingPeriod(!isTrial && currentBillingPeriod ? currentBillingPeriod : 'annual');
    setCheckoutQuote(null);
    setEnterpriseQuote(null);
    setEnterpriseEmployeeCount(currentEmployeeCount ? String(currentEmployeeCount) : '');
    setCheckoutSeatCount(String(defaultSeatCount));
    setPricingStep('plan');
    setIncludedEntitlementTab('knowledge-base');
    setEditingBillingAddress(false);
    setShowPricingDialog(true);
  };

  const planCards = (
    <div className="grid items-stretch gap-6 md:grid-cols-3">
      {pricingTiers.map((tier) => {
        const Icon = tier.icon;
        const isCurrentPlan = currentTierId === tier.id && hasPurchasedPlan;
        const isProvisionedUnpaid = currentTierId === tier.id && !isTrial && !hasPurchasedPlan;
        const isCustomPlan = !tier.monthlyPrice;
        const planLimitLabel = (value, formatter = formatCompactNumber) => (
          isCustomPlan ? 'Sized quote' : formatLimitValue(value, formatter)
        );

        return (
          <Card
            key={tier.id}
            className={`relative flex h-full flex-col ${tier.popular ? 'border-purple-500 shadow-lg' : ''} ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}
          >
            {tier.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-purple-500 text-white">Most Popular</Badge>
              </div>
            )}
            {isCurrentPlan && (
              <div className="absolute -top-3 right-4">
                <Badge variant="outline" className="bg-background">Current Plan</Badge>
              </div>
            )}
            {isProvisionedUnpaid && (
              <div className="absolute -top-3 right-4">
                <Badge variant="outline" className="bg-background border-warning/40 text-warning">Payment required</Badge>
              </div>
            )}
            <CardHeader className="min-h-[156px]">
              <div className={`w-10 h-10 rounded-lg ${tier.bgColor} flex items-center justify-center mb-2`}>
                <Icon className={`h-5 w-5 ${tier.color}`} />
              </div>
              <CardTitle>{tier.name}</CardTitle>
              <CardDescription className="min-h-[40px]">{tier.description}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col space-y-4">
              <div className="min-h-[76px]">
                {tier.monthlyPrice ? (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold">${tier.monthlyPrice.toFixed(2)}</span>
                      <span className="text-muted-foreground">/user/month</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      ${tier.annualPrice.toFixed(2)}/user/month billed annually (save 10%)
                    </p>
                  </>
                ) : (
                  <div className="text-3xl font-bold">Sized quote</div>
                )}
              </div>

              <div className="min-h-[36px] border-b pb-2 text-sm text-muted-foreground">
                {isCustomPlan
                  ? 'Enter seats for pricing'
                  : `Up to ${
                      tier.employee_limit === -1 || tier.employee_limit == null
                      || tier.content_team_limit === -1 || tier.content_team_limit == null
                        ? 'unlimited'
                        : (tier.employee_limit + tier.content_team_limit).toLocaleString()
                    } seats (employees + content team)`}
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">Eva People credits / seat</p>
                  <p className="font-medium">{planLimitLabel(tier.tokens_per_seat_month, formatTokens)}</p>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">KB monthly credits</p>
                  <p className="font-medium">{planLimitLabel(tier.kb_monthly_token_limit, formatTokens)}</p>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">Documents</p>
                  <p className="font-medium">{planLimitLabel(tier.document_limit)}</p>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">Pages / document</p>
                  <p className="font-medium">{planLimitLabel(tier.max_chunks_per_document)}</p>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">Topics</p>
                  <p className="font-medium">{planLimitLabel(tier.topics_limit)}</p>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">Integrations</p>
                  <p className="font-medium">{planLimitLabel(tier.integrations_limit)}</p>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">Monthly exports</p>
                  <p className="font-medium">{planLimitLabel(tier.monthly_export_limit)}</p>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">Scheduled reports</p>
                  <p className="font-medium">{planLimitLabel(tier.scheduled_reports_limit)}</p>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                  <p className="text-xs text-muted-foreground">Notifications</p>
                  <p className="font-medium">{planLimitLabel(tier.notification_limit)}</p>
                </div>
              </div>

              <ul className="min-h-[112px] flex-1 space-y-2">
                {tier.features.slice(0, 6).map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    {feature.included ? (
                      <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <X className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    )}
                    <span className={feature.included ? '' : 'text-muted-foreground'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {isAdmin && (
                <div className="mt-auto pt-2">
                  <Button
                    className="w-full"
                    variant={tier.popular ? 'default' : 'outline'}
                    onClick={() => openPricingDialog(tier.id)}
                    disabled={isCurrentPlan && !isTrial}
                  >
                    {isCurrentPlan && !isTrial
                      ? 'Current Plan'
                      : isProvisionedUnpaid
                        ? 'Complete Payment'
                        : 'Select Plan'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  return (
    <PageShell maxWidth="7xl" data-testid="billing-page">
      {/* Page Banner */}
      <PageBanner pageKey="settings-billing" />

      <PageHeader
        title="Billing & Subscription"
        icon={CreditCard}
        description="Manage your subscription, view payment history, and update billing settings"
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 sm:w-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="tab-subscription">Subscription</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Compare Plans</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Payment History</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mx-auto w-full max-w-6xl space-y-6">
          {/* Subscription Status Card */}
          <Card className={
            (trialExpired && isTrial) || subscriptionState === 'expired' || displayStatus === 'deactivated'
              ? 'border-destructive'
              : subscriptionState === 'grace' || !subscriptionValid || requiresEnterpriseConfiguration
                ? 'border-amber-500'
                : ''
          }>
            <CardHeader>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="flex flex-wrap items-center gap-2">
                    {billingStatus?.tenant_name || tenant?.display_name}
                    <StatusBadge status={billingStatusForDisplay} />
                  </CardTitle>
                  <CardDescription>
                    {currentTier} Plan
                  </CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={() => openPricingDialog()} data-testid="upgrade-btn" className="w-full sm:w-auto">
                    <Sparkles className="h-4 w-4 mr-2" />
                    {isTrial ? 'Upgrade Now' : 'Change Plan'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Trial Status Banner - Only show for trial users */}
              {isTrial && (
                <div className={`p-4 rounded-lg ${trialExpired ? 'bg-destructive/10 border border-destructive/20' : 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800'}`}>
                  <div className="flex items-start gap-3">
                    {trialExpired ? (
                      <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                    ) : (
                      <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-medium ${trialExpired ? 'text-destructive' : 'text-amber-800 dark:text-amber-200'}`}>
                        {trialExpired 
                          ? 'Trial Expired' 
                          : `${trialDaysRemaining} days remaining in trial`}
                      </h4>
                      <p className={`text-sm ${trialExpired ? 'text-destructive/80' : 'text-amber-700 dark:text-amber-300'}`}>
                        {trialExpired 
                          ? 'Upgrade now to continue using Eva Insights with all features.'
                          : `Your trial ends on ${formatDate(billingStatus?.trial?.ends_at)}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {requiresEnterpriseConfiguration && (
                <div className="rounded-lg border border-amber-300 bg-amber-500/10 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
                    <div className="space-y-1">
                      <h4 className="font-medium text-amber-800 dark:text-amber-200">
                        Enterprise limits required
                      </h4>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        This Enterprise tenant is not valid until Superadmin configures numeric limits. Only the Vistry tenant can use unlimited capacity.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center justify-end">
                  <div className="inline-flex rounded-lg bg-muted p-1" aria-label="Entitlement layout">
                    <Button
                      type="button"
                      variant={entitlementLayout === 'table' ? 'secondary' : 'ghost'}
                      size="icon"
                      className="h-9 w-9"
                      aria-label="Table layout"
                      aria-pressed={entitlementLayout === 'table'}
                      onClick={() => setEntitlementLayout('table')}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant={entitlementLayout === 'cards' ? 'secondary' : 'ghost'}
                      size="icon"
                      className="h-9 w-9"
                      aria-label="Card layout"
                      aria-pressed={entitlementLayout === 'cards'}
                      onClick={() => setEntitlementLayout('cards')}
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                  </div>
	                </div>
	                {entitlementSections.map((section) => {
	                  const SectionIcon = section.icon;
	                  return (
	                    <section key={section.key} className="rounded-lg border bg-muted/20">
	                      <button
	                        type="button"
	                        className="flex w-full items-center justify-between gap-4 rounded-lg p-4 text-left transition-colors hover:bg-muted/30"
	                        aria-expanded={!collapsedEntitlementSections[section.key]}
	                        aria-controls={`billing-entitlements-${section.key}`}
	                        onClick={() => toggleEntitlementSection(section.key)}
	                      >
	                        <div className="flex min-w-0 items-center gap-3">
	                          {SectionIcon && (
	                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
	                              <SectionIcon className="h-5 w-5" />
	                            </div>
	                          )}
	                          <div className="min-w-0">
	                            <h3 className="text-base font-semibold text-foreground">
	                              {section.title}
	                            </h3>
	                            <p className="text-sm text-muted-foreground">
	                              {section.meters.length} entitlement{section.meters.length === 1 ? '' : 's'}
	                            </p>
	                          </div>
	                        </div>
	                        <ChevronDown
	                          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${collapsedEntitlementSections[section.key] ? '-rotate-90' : ''}`}
	                        />
	                      </button>
                    {!collapsedEntitlementSections[section.key] && (
                      entitlementLayout === 'cards' ? (
                        <div
                          id={`billing-entitlements-${section.key}`}
                          className="grid gap-4 border-t p-4 md:grid-cols-2 xl:grid-cols-3"
                        >
                          {section.meters.map((meter) => (
                            <LimitMeter
                              key={meter.key}
                              title={meter.title}
                              used={meter.used}
                              limit={getMeterLimit(meter)}
                              icon={meter.icon}
                              formatter={meter.formatter}
                              caption={meter.caption}
                              percentUsed={meter.percentUsed}
                              displayType={meter.displayType}
                              testId={meter.testId}
                              isConfigured={isMeterConfigured(meter)}
                            />
                          ))}
                        </div>
                      ) : (
	                        <div id={`billing-entitlements-${section.key}`} className="border-t">
	                          <Table className="table-fixed">
	                            <EntitlementTableColumns />
	                            <TableHeader>
                              <TableRow>
                                <TableHead>Limit</TableHead>
                                <TableHead>Usage</TableHead>
                                <TableHead>Included</TableHead>
                                <TableHead>Remaining</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {section.meters.map((meter) => {
                                const Icon = meter.icon;
                                const limit = getMeterLimit(meter);
                                const formatter = meter.formatter || formatCompactNumber;
                                const state = getMeterState(meter);
                                const shownPercent = Math.min(state.percent, 100);
                                const isRule = meter.displayType === 'rule';
                                const isConfigured = isMeterConfigured(meter);

                                return (
                                  <TableRow key={meter.key} data-testid={meter.testId}>
                                    <TableCell>
                                      <div className="flex min-w-0 items-center gap-3">
                                        {Icon && (
                                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                            <Icon className="h-4 w-4" />
                                          </div>
                                        )}
                                        <div className="min-w-0">
                                          <p className="font-medium text-foreground">{meter.title}</p>
                                          {meter.caption && (
                                            <p className="text-sm text-muted-foreground">{meter.caption}</p>
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {getMeterUsageLabel(meter, limit, state)}
                                    </TableCell>
                                    <TableCell>
                                      {state.key === 'not_configured'
                                        ? 'Not set'
                                        : isRule
                                          ? formatLimitValue(limit, formatter)
                                          : formatLimitValue(limit, formatter)}
                                    </TableCell>
                                    <TableCell>
                                      <div className="min-w-40 space-y-2">
                                        <p className="text-sm text-muted-foreground">
                                          {getMeterRemainingLabel(meter, limit, state)}
                                        </p>
                                        {!isRule && isConfigured && state.key !== 'unlimited' && (
                                          <div className="h-2 overflow-hidden rounded-full bg-muted">
                                            <div
                                              data-testid={`${meter.key}-progress-fill`}
                                              className={`h-full rounded-full ${
                                                state.key === 'exceeded'
                                                  ? 'bg-destructive'
                                                  : state.key === 'critical'
                                                    ? 'bg-orange-500'
                                                    : state.key === 'warning'
                                                      ? 'bg-warning'
                                                      : 'bg-emerald-500'
                                              }`}
                                              style={{ width: `${shownPercent}%` }}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className={getMeterBadgeClass(state.key, meter.displayType)}>
                                        {getMeterBadgeLabel(meter, state)}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )
                    )}
	                  </section>
	                  );
	                })}

              </div>

              {/* Referral Credits */}
              {credits.balance > 0 && (
                <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
                        <Sparkles className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-emerald-800 dark:text-emerald-200">
                          Referral Balance Available
                        </h4>
                        <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-0.5">
                          Use your referral balance as a discount on your next subscription payment
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-600">${credits.balance.toFixed(2)}</p>
                      <p className="text-xs text-emerald-600/70">Lifetime earned: ${credits.lifetime_earned?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="mx-auto w-full max-w-6xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Subscription
              </CardTitle>
              <CardDescription>
                Renewal timing, payment method, and subscription controls
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border bg-background/50 p-4">
                  <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {{
                      trial_ends: 'Trial Ends',
                      next_billing: 'Next Billing',
                      renews: 'Renews',
                      access_until: 'Access Until',
                      ended: 'Access Ended',
                      none_on_file: 'Renewal',
                    }[billingStatus?.subscription?.display_date_kind] || 'Access Until'}
                  </div>
                  <p className="text-lg font-semibold" data-testid="subscription-end-date">
                    {billingStatus?.subscription?.display_date_kind === 'none_on_file'
                      ? 'No renewal date on file'
                      : formatDate(billingStatus?.subscription?.ends_at)}
                  </p>
                  {billingStatus?.subscription?.display_date_kind === 'none_on_file' && isAdmin && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Contact support if you were expecting a renewal date here.
                    </p>
                  )}
                  {!isTrial && billingStatus?.subscription?.has_stripe_subscription && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {billingStatus?.subscription?.billing_period === 'monthly' ? 'Monthly billing' : billingStatus?.subscription?.billing_period === 'annual' ? 'Annual billing' : ''}
                      {billingStatus?.subscription?.auto_renew === false && ' • Canceling at period end'}
                    </p>
                  )}
                </div>

                {billingStatus?.subscription?.has_stripe_subscription && isAdmin && (
                  <div className="rounded-lg border bg-background/50 p-4">
                    <p className="font-medium">Manage Subscription</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {renewalPaymentMethod?.label
                        ? `Renewals use ${renewalPaymentMethod.label}`
                        : 'Update payment method, view invoices, or manage your subscription'}
                    </p>
                    <Button variant="outline" size="sm" onClick={handleManageBilling} data-testid="billing-portal-btn" className="mt-4">
                      <CreditCard className="h-4 w-4 mr-1" />
                      Billing Portal
                    </Button>
                  </div>
                )}
              </div>

              {subscriptionValid && billingStatus?.subscription?.has_stripe_subscription && isAdmin && shouldShowPaymentMethodWarning && (
                <div className="flex flex-col gap-4 rounded-lg border border-amber-300 bg-amber-500/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200">
                        Add a payment method for renewal
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        Auto-renewal is on, but Stripe does not have a default payment method for this subscription.
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleManageBilling} data-testid="payment-method-warning-btn">
                    <CreditCard className="h-4 w-4 mr-1" />
                    Add Payment Method
                  </Button>
                </div>
              )}

              {subscriptionValid && billingStatus?.subscription?.has_stripe_subscription && isAdmin && (
                <div className="flex flex-col gap-4 rounded-lg border border-dashed p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium">
                      {billingStatus?.subscription?.auto_renew === false
                        ? 'Subscription Set to Cancel'
                        : 'Subscription Active'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {billingStatus?.subscription?.auto_renew === false
                        ? `Access until ${formatDate(billingStatus?.subscription?.ends_at)}`
                        : `Next billing: ${formatDate(billingStatus?.subscription?.ends_at)}`}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    {billingStatus?.subscription?.auto_renew === false ? (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleReactivateSubscription}
                        data-testid="reactivate-subscription-btn"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Reactivate Subscription
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleCancelSubscription}
                        data-testid="cancel-subscription-btn"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compare Plans Tab */}
        <TabsContent value="plans" className="mx-auto w-full max-w-6xl space-y-6">
          <section className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">Compare Plans</h2>
              <p className="text-muted-foreground">
                Compare your current plan with available options.
              </p>
            </div>
            {planCards}
          </section>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Payment History
              </CardTitle>
              <CardDescription>
                View all past payments and download invoices
              </CardDescription>
            </CardHeader>
            <CardContent>
              {paymentHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No payment history yet</p>
                  <p className="text-sm">Payments will appear here after your first transaction</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="min-w-[760px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentHistory.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{formatDate(payment.date)}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{formatCellValue(payment.package_name)}</p>
                              <p className="text-xs text-muted-foreground">{formatCellValue(payment.user_count)} users</p>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">{formatCellValue(payment.billing_period)}</TableCell>
                          <TableCell><StatusBadge status={payment.status} /></TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(payment.amount, payment.currency)}
                          </TableCell>
                          <TableCell>
                            {payment.invoice_url && (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={payment.invoice_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Pricing Dialog */}
      <Dialog
        open={showPricingDialog}
        onOpenChange={(open) => {
          setShowPricingDialog(open);
          if (!open) {
            setClientSecret(null);
            setPendingSubscriptionId(null);
            setPaymentError('');
          }
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto overflow-x-hidden border-border/70 bg-card p-0 shadow-2xl">
          <DialogHeader className="border-b bg-card px-6 py-6 text-left">
            <div className="flex items-start gap-4 pr-8">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <CreditCard className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-2xl font-semibold leading-8">Choose Your Plan</DialogTitle>
                <DialogDescription className="mt-1 text-base">
                  Select a plan, seat count, billing period, and payment method.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="border-b bg-muted/20 px-6 py-5">
            <div className="flex items-center justify-between gap-3">
              {pricingSteps.map((step, index) => (
                <div key={step.key} className="flex min-w-0 flex-1 items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPricingStep(step.key)}
                    className={`group flex min-w-0 items-center gap-3 rounded-xl px-2 py-1.5 text-left transition ${
                      pricingStep === step.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-semibold transition ${
                        pricingStep === step.key
                          ? 'bg-primary/15 text-primary'
                          : index < pricingStepIndex
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground group-hover:bg-muted/80'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] font-semibold uppercase tracking-wide">
                        Step {index + 1}
                      </span>
                      <span className="block truncate text-base font-semibold">{step.label}</span>
                    </span>
                  </button>
                  {index < pricingSteps.length - 1 && (
                    <ChevronRight className="hidden h-5 w-5 shrink-0 text-muted-foreground/60 sm:block" />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6 px-6 py-6">
            <div className="rounded-xl bg-card">
              <div className="mb-4">
                <h3 className="text-xl font-semibold">{pricingSteps[pricingStepIndex]?.label}</h3>
                <p className="mt-1 text-base text-muted-foreground">{pricingSteps[pricingStepIndex]?.description}</p>
              </div>

              {pricingStep === 'plan' && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase text-muted-foreground tracking-wide">Plan</Label>
                <RadioGroup
                  value={selectedTier}
                  onValueChange={(value) => {
                    setSelectedTier(value);
                    setCheckoutQuote(null);
                    setEnterpriseQuote(null);
                  }}
                  className="space-y-2"
                >
                  {pricingTiers.filter((tier) => tier.id !== 'enterprise' || tier.id === currentTierId).map((tier, idx) => {
                    const tierIsCurrent = !isTrial && tier.id === currentTierId;
                    const tierIsDowngrade = !isTrial && currentTierIndex !== -1 && idx < currentTierIndex;
                    const tierIsUpgrade = !isTrial && currentTierIndex !== -1 && idx > currentTierIndex;
                    const planBadge = tierIsCurrent ? 'Current' : tierIsDowngrade ? 'Downgrade' : tierIsUpgrade ? 'Upgrade' : null;

                    return (
                      <div key={tier.id}>
                        <RadioGroupItem value={tier.id} id={`plan-${tier.id}`} className="peer sr-only" />
                        <Label
                          htmlFor={`plan-${tier.id}`}
                          className="flex items-center justify-between gap-3 rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent peer-data-[state=checked]:border-primary cursor-pointer"
                        >
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold">{tier.name}</span>
                              {planBadge && (
                                <Badge variant="outline" className="text-[10px]">
                                  {planBadge}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {tier.id === 'enterprise'
                                ? 'Enter employee count'
                                : `Up to ${tier.employee_limit.toLocaleString()} employees`}
                            </p>
                          </div>
                          <span className="font-mono text-sm">{tier.monthlyPrice ? `$${tier.monthlyPrice}/user` : 'Size plan'}</span>
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
                </div>
              )}

              {pricingStep === 'seats' && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-primary/30 bg-primary/10 p-4">
                    <p className="text-base font-semibold text-foreground">Seat count is required for checkout</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Choose the total employee capacity for this subscription term. This number drives pricing, limits, and Stripe billing.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="checkout-seat-count" className="text-sm font-semibold uppercase tracking-wide text-foreground">
                      Total seats to purchase <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="checkout-seat-count"
                      type="number"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      value={checkoutSeatCount}
                      className="h-14 text-lg"
                      onChange={(event) => {
                        setCheckoutSeatCount(event.target.value);
                        setEnterpriseEmployeeCount(event.target.value);
                        setCheckoutQuote(null);
                        setEnterpriseQuote(null);
                      }}
                      placeholder={`Minimum ${minimumSeatCount.toLocaleString()} ${minimumSeatCount === 1 ? 'seat' : 'seats'}`}
                      aria-describedby="checkout-seat-count-help"
                      aria-invalid={Boolean(seatCountBlocker)}
                    />
                    <p id="checkout-seat-count-help" className="text-sm text-muted-foreground">
                      Enter the total seats you want available, not just the additional seats.
                    </p>
                    {requestedSeatCount > 0 && (
                      <p className="text-sm font-medium text-foreground">
                        {isTrial
                          ? `New subscription — purchasing ${requestedSeatCount.toLocaleString()} ${requestedSeatCount === 1 ? 'seat' : 'seats'}.`
                          : additionalSeats > 0
                            ? `That's ${additionalSeats.toLocaleString()} additional ${additionalSeats === 1 ? 'seat' : 'seats'} on top of your current ${currentPurchasedSeats.toLocaleString()}.`
                            : additionalSeats < 0
                              ? `That's ${Math.abs(additionalSeats).toLocaleString()} fewer ${Math.abs(additionalSeats) === 1 ? 'seat' : 'seats'} than your current ${currentPurchasedSeats.toLocaleString()}; reductions take effect at renewal.`
                              : `No change from your current ${currentPurchasedSeats.toLocaleString()} ${currentPurchasedSeats === 1 ? 'seat' : 'seats'}.`}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-md border bg-muted/30 p-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Current employees</p>
                      <p className="mt-1 text-xl font-semibold">{currentEmployeeCount.toLocaleString()}</p>
                    </div>
                    <div className="rounded-md border bg-muted/30 p-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Current seats purchased</p>
                      <p className="mt-1 text-xl font-semibold">
                        {isTrial ? 'None (trial)' : currentPurchasedSeats.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-md border bg-muted/30 p-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Selected seats</p>
                      <p className="mt-1 text-xl font-semibold">{requestedSeatCount.toLocaleString()}</p>
                    </div>
                    <div className="rounded-md border bg-muted/30 p-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Plan capacity</p>
                      <p className="mt-1 text-xl font-semibold">
                        {selectedIsEnterprise ? 'Sized' : `Up to ${selectedTierEmployeeLimit?.toLocaleString() || 0}`}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Seats represent employee capacity for this subscription term. Reductions should be scheduled for renewal; immediate checkout must cover current employees.
                  </p>
                  {seatCountBlocker && (
                    <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      {seatCountBlocker}
                    </div>
                  )}
                </div>
              )}

              {pricingStep === 'billing' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-muted-foreground tracking-wide">Billing</Label>
                    <RadioGroup
                      value={billingPeriod}
                      onValueChange={(value) => {
                        setBillingPeriod(value);
                        setCheckoutQuote(null);
                        setEnterpriseQuote(null);
                      }}
                      className="space-y-2"
                    >
                      <div>
                        <RadioGroupItem value="annual" id="bill-annual" className="peer sr-only" />
                        <Label
                          htmlFor="bill-annual"
                          className="flex cursor-pointer items-center justify-between rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent peer-data-[state=checked]:border-primary"
                        >
                          <div>
                            <span className="font-semibold">Annual</span>
                            <p className="text-xs text-muted-foreground">Pay once, save 10%</p>
                          </div>
                          <div className="flex flex-wrap justify-end gap-2">
                            {currentBillingPeriod === 'annual' && (
                              <Badge variant="outline" className="text-[10px]">Current</Badge>
                            )}
                            <Badge className="bg-green-500 text-white">Best Value</Badge>
                          </div>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="monthly" id="bill-monthly" className="peer sr-only" />
                        <Label
                          htmlFor="bill-monthly"
                          className="flex cursor-pointer items-center justify-between rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent peer-data-[state=checked]:border-primary"
                        >
                          <div>
                            <span className="font-semibold">Monthly</span>
                            <p className="text-xs text-muted-foreground">Billed monthly</p>
                          </div>
                          {currentBillingPeriod === 'monthly' && (
                            <Badge variant="outline" className="text-[10px]">Current</Badge>
                          )}
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {availablePaymentMethods.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase text-muted-foreground tracking-wide">Payment</Label>
                      <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">
                        <div>
                          <RadioGroupItem value="card" id="pay-card" className="peer sr-only" />
                          <Label
                            htmlFor="pay-card"
                            className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent peer-data-[state=checked]:border-primary"
                          >
                            <CreditCard className="h-4 w-4" />
                            Card
                          </Label>
                        </div>
                        <div>
                          <RadioGroupItem value="ach" id="pay-ach" className="peer sr-only" />
                          <Label
                            htmlFor="pay-ach"
                            className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-muted bg-popover p-3 hover:bg-accent peer-data-[state=checked]:border-primary"
                          >
                            <Building2 className="h-4 w-4" />
                            ACH
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}
                </div>
              )}

              {pricingStep === 'review' && selectedTier && pricingTiers.find(t => t.id === selectedTier) && !selectedIsEnterprise && (
                <div className="rounded-lg bg-muted/50 p-4">
                <div className="space-y-2 font-mono text-sm">
                  {(() => {
                    const tier = pricingTiers.find(t => t.id === selectedTier);
                    const pricePerUser = checkoutQuote?.price_per_user ?? (billingPeriod === 'annual' ? tier.annualPrice : tier.monthlyPrice);
                    const billableSeats = checkoutQuote?.billable_seats ?? requestedSeatCount ?? 1;
                    const months = checkoutQuote?.months ?? (billingPeriod === 'annual' ? 12 : 1);
                    const subtotal = checkoutQuote?.subtotal ?? (pricePerUser * billableSeats * months);
                    const creditDiscount = checkoutQuote?.credit_discount ?? (applyCredits ? Math.min(credits.balance, Math.max(subtotal - 0.5, 0)) : 0);
                    const total = checkoutQuote?.total_before_tax ?? (subtotal - creditDiscount);
                    
                    return (
                      <>
	                        <div className="flex justify-between items-center">
	                          <span className="text-muted-foreground">
	                            {formatMoney(pricePerUser)} × {billableSeats.toLocaleString()} seats × {months} {months === 1 ? 'month' : 'months'}
	                          </span>
	                          <span className="text-right">{formatMoney(subtotal)}</span>
	                        </div>
                        
                        {credits.balance > 0 && (
                          <div className="flex justify-between items-center text-emerald-600">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={applyCredits}
                                onChange={(e) => setApplyCredits(e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              <span>Referral balance</span>
                            </label>
	                            <span>- {formatMoney(creditDiscount)}</span>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center text-muted-foreground">
                          <span>Tax</span>
                          <span>At checkout</span>
                        </div>
                        
                        <div className="border-t pt-2 flex justify-between items-center font-semibold text-base">
                          <span>Total</span>
	                          <span>{quoteLoading ? 'Calculating...' : `${formatMoney(total)}${billingPeriod === 'annual' ? '/yr' : '/mo'}`}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {(clientCheckoutBlocker || checkoutQuote?.blockers?.length > 0) && (
                  <div className="mt-3 rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {clientCheckoutBlocker || checkoutQuote.blockers[0].message}
                  </div>
                )}

                {!clientCheckoutBlocker && selectedIsUpgrade && (
                  <div className="mt-3 rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600">
                    This will upgrade your subscription from {currentTier} to {selectedTierData?.name}.
                  </div>
                )}

                {!clientCheckoutBlocker && selectedIsSeatIncrease && (
                  <div className="mt-3 rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600">
                    This will increase your subscription from {currentPaidSeatCount.toLocaleString()} to {requestedSeatCount.toLocaleString()} seats and invoice the prorated difference.
                  </div>
                )}

                {!clientCheckoutBlocker && selectedIsSeatDecrease && (
                  <div className="mt-3 rounded border border-primary/30 bg-primary/10 p-3 text-sm text-primary">
                    This will reduce your next renewal invoice from {currentPaidSeatCount.toLocaleString()} to {requestedSeatCount.toLocaleString()} seats. No credit is issued for the current term.
                  </div>
                )}

                {!clientCheckoutBlocker && selectedIsDowngrade && checkoutQuote?.can_checkout === true && (
                  <div className="mt-3 rounded border border-primary/30 bg-primary/10 p-3 text-sm text-primary">
                    This will downgrade your subscription from {currentTier} to {selectedTierData?.name}. It takes effect at your next renewal — you keep full {currentTier} access until then, and no credit is issued for the current term.
                  </div>
                )}

                {!clientCheckoutBlocker && selectedIsBillingChange && (
                  <div className="mt-3 rounded border border-primary/30 bg-primary/10 p-3 text-sm text-primary">
                    This will change your {currentTier} billing period from {currentBillingPeriod} to {billingPeriod}.
                  </div>
                )}
                
                {billingPeriod === 'annual' && (
                  <p className="text-xs text-green-600 mt-2 text-center">
                    Save 10% with annual billing
                  </p>
                )}
                </div>
              )}

              {pricingStep === 'review' && selectedIsEnterprise && (
                <div className="rounded-lg bg-muted/50 p-4">
                <div className="space-y-3 text-sm">
                  {!selectedEnterpriseConfigured && (
                    <div className="flex items-center justify-between gap-3 rounded-md border bg-background/50 p-3">
                      <div>
                        <p className="font-medium">Enterprise seats</p>
                        <p className="text-xs text-muted-foreground">Sized from the seats selected in step 2.</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fetchEnterpriseQuote(requestedSeatCount)}
                        disabled={enterpriseQuoteLoading || Boolean(seatCountBlocker)}
                      >
                        {enterpriseQuoteLoading ? 'Sizing...' : 'Refresh sizing'}
                      </Button>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Enterprise sizing</span>
                    <span className="font-medium">
                      {selectedEnterpriseConfigured
                        ? `${planLimits.employee_limit?.toLocaleString() || billingStatus?.usage?.employees || 1} employees`
                        : enterpriseQuote
                          ? `${enterpriseQuote.employee_count.toLocaleString()} employees`
                          : 'Size pending'}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between items-center font-semibold">
                    <span>{selectedEnterpriseConfigured ? 'Contracted plan' : 'Total'}</span>
                    <span>
                      {selectedEnterpriseConfigured
                        ? 'Configured'
                        : enterpriseQuote
	                          ? `${formatMoney(enterpriseQuote.total_before_tax)}${billingPeriod === 'annual' ? '/yr' : '/mo'}`
	                          : 'Size to price'}
	                    </span>
	                  </div>
	                  {enterpriseQuote && !selectedEnterpriseConfigured && (
	                    <div className="flex justify-between items-center text-muted-foreground">
	                      <span>
	                        {formatMoney(enterpriseQuote.price_per_user)} × {enterpriseQuote.employee_count.toLocaleString()} seats × {enterpriseQuote.months} {enterpriseQuote.months === 1 ? 'month' : 'months'}
	                      </span>
	                      <span>{formatMoney(enterpriseQuote.subtotal)}</span>
	                    </div>
	                  )}
                  {!selectedEnterpriseConfigured && (
                    <div className="mt-3 rounded border border-primary/30 bg-primary/10 p-3 text-sm text-primary">
                      Enter an employee count to size a Sales estimate. Enterprise requires a conversation with our Sales team to purchase.
                    </div>
                  )}
                  {selectedEnterpriseConfigured && selectedIsCurrentPlan && (
                    <div className="mt-3 rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      You are already on Enterprise. Select another option only if you need a plan change.
                    </div>
                  )}
                </div>
                </div>
              )}

              {pricingStep === 'payment' && !selectedIsEnterprise && !selectedIsDowngrade && !selectedIsSeatDecrease && savedBillingAddress && !editingBillingAddress && (
                <div className="rounded-lg border bg-muted/30 p-4" data-testid="billing-address-summary">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Billing address</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {savedBillingAddress.name && <>{savedBillingAddress.name}<br /></>}
                        {savedBillingAddress.line1}{savedBillingAddress.line2 ? `, ${savedBillingAddress.line2}` : ''}<br />
                        {savedBillingAddress.city}{savedBillingAddress.state ? `, ${savedBillingAddress.state}` : ''} {savedBillingAddress.postal_code}<br />
                        {savedBillingAddress.country}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingBillingAddress(true)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              )}

              {pricingStep === 'payment' && !selectedIsEnterprise && !selectedIsDowngrade && !selectedIsSeatDecrease && needsAddressInput && (
                <div className="rounded-lg border bg-muted/30 p-4 space-y-3" data-testid="billing-address-form">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Billing address</p>
                      <p className="text-xs text-muted-foreground">Required once so Stripe can calculate tax on this order.</p>
                    </div>
                    {savedBillingAddress && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditingBillingAddress(false)}>
                        Cancel
                      </Button>
                    )}
                  </div>
                  <div ref={addressElementContainerRef} data-testid="stripe-address-element" />
                </div>
              )}

              {pricingStep === 'payment' && clientSecret && (
                <div className="mt-4 rounded-lg border bg-card p-4" data-testid="payment-details-panel">
                  <h4 className="mb-3 text-sm font-medium">Payment details</h4>
                  <div ref={paymentElementContainerRef} data-testid="stripe-payment-element" />
                  {paymentError && (
                    <p className="mt-3 text-sm text-destructive">{paymentError}</p>
                  )}
                </div>
              )}

              {pricingStep === 'payment' && !clientSecret && (checkoutLoading || quoteLoading) && (
                <div className="mt-4 rounded-lg border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Preparing checkout...
                </div>
              )}

              {pricingStep === 'review' && (
                <div className="mt-4 rounded-lg border bg-muted/30 p-4" data-testid="renewal-terms">
                  <div className="flex items-start gap-3">
                    <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="space-y-1.5 text-sm text-muted-foreground">
                      <p className="font-medium text-foreground">Auto-renewal &amp; billing terms</p>
                      <p>
                        {`This is an auto-renewing ${billingPeriod === 'annual' ? 'annual' : 'monthly'} subscription that renews automatically ${billingPeriod === 'annual' ? 'every year' : 'every month'} until you cancel. You can cancel anytime before your renewal date.`}
                      </p>
                      <p>
                        Each time your plan renews, we email a payment confirmation and receipt to your billing contacts.
                      </p>
                      <p className="text-xs">
                        By continuing, you agree to our{' '}
                        <a
                          href="https://vistry.ai/tos"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Terms of Service
                        </a>.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {pricingStep === 'plan' && selectedTier && pricingTiers.find(t => t.id === selectedTier) && (
                <div className="mt-4 rounded-lg border bg-card p-3">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-medium">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  What's included in {pricingTiers.find(t => t.id === selectedTier)?.name}
                </h4>
	                  <Tabs value={includedEntitlementTab} onValueChange={setIncludedEntitlementTab}>
	                  <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-5">
	                    {includedEntitlementSections.map((section) => {
	                      const SectionIcon = section.icon;
	                      return (
	                        <TabsTrigger key={section.key} value={section.key} className="gap-1.5 px-2 py-1.5 text-xs">
	                          {SectionIcon && <SectionIcon className="h-3.5 w-3.5" />}
	                          {section.title}
	                        </TabsTrigger>
	                      );
	                    })}
	                  </TabsList>
	                  {includedEntitlementSections.map((section) => {
	                    const SectionIcon = section.icon;
	                    return (
	                    <TabsContent key={section.key} value={section.key} className="mt-3">
	                      <div className="rounded-md border bg-muted/30 p-3">
	                        <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
		                          <div>
		                            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
		                              {SectionIcon && <SectionIcon className="h-3.5 w-3.5" />}
		                              <span>{section.title}</span>
		                            </p>
                            <p className="text-xs text-muted-foreground">
                              {section.description}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {section.items.length} entitlement{section.items.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                          {section.items.map((entitlement) => {
                            const Icon = entitlement.icon;
                            return (
                              <div key={entitlement.key} className="flex min-w-0 items-center gap-2">
                                <Icon className={`h-3.5 w-3.5 shrink-0 ${entitlement.color}`} />
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold leading-5">
                                    {formatIncludedLimit(entitlement.key, entitlement.formatter)}
                                  </p>
                                  <p className="truncate text-[11px] leading-4 text-muted-foreground">{entitlement.label}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
	                    </TabsContent>
	                    );
	                  })}
                </Tabs>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="grid grid-cols-2 gap-4 border-t bg-card px-6 py-5 sm:grid-cols-3 sm:gap-4">
            <Button variant="outline" className="h-14 text-base" onClick={() => setShowPricingDialog(false)}>
              Cancel
            </Button>
            {pricingStepIndex > 0 && (
              <Button
                variant="outline"
                className="h-14 text-base"
                onClick={() => setPricingStep(pricingSteps[pricingStepIndex - 1].key)}
                disabled={checkoutLoading}
              >
                Back
              </Button>
            )}
            {pricingStep !== 'review' && pricingStep !== 'payment' ? (
              <Button
                className={`${pricingStepIndex > 0 ? 'col-span-2 sm:col-span-1' : 'sm:col-span-2'} h-14 text-base`}
                onClick={async () => {
                  if (!canAdvancePricingStep) return;
                  const nextStep = pricingSteps[pricingStepIndex + 1]?.key || 'review';
                  if (nextStep === 'review') {
                    if (selectedIsEnterprise && !selectedEnterpriseConfigured) {
                      await fetchEnterpriseQuote(requestedSeatCount);
                    } else {
                      await fetchCheckoutQuote();
                    }
                  }
                  setPricingStep(nextStep);
                }}
                disabled={!canAdvancePricingStep || quoteLoading || enterpriseQuoteLoading}
              >
                {quoteLoading || enterpriseQuoteLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  'Next'
                )}
              </Button>
            ) : pricingStep === 'review' && selectedIsEnterprise ? (
            // EIP-273: Enterprise is a negotiated contract, not a self-serve
            // purchase — route to Sales instead of Stripe checkout.
            <Button
              className={`${pricingStepIndex > 0 ? 'col-span-2 sm:col-span-1' : 'sm:col-span-2'} h-14 text-base`}
              onClick={() => window.open('https://www.vistry.ai/demo', '_blank', 'noopener,noreferrer')}
            >
              Contact Sales
            </Button>
            ) : pricingStep === 'review' && (selectedIsDowngrade || selectedIsSeatDecrease) ? (
            // Scheduled at-renewal changes don't create a new Stripe Subscription
            // immediately (no card/address needed), so they submit directly from
            // Review rather than advancing to the Payment step.
            <Button
              className={`${pricingStepIndex > 0 ? 'col-span-2 sm:col-span-1' : 'sm:col-span-2'} h-14 text-base`}
              onClick={handleCheckout}
              disabled={
                checkoutLoading
                || quoteLoading
                || Boolean(seatCountBlocker)
                || Boolean(clientCheckoutBlocker)
                || (selectedIsDowngrade && checkoutQuote?.can_checkout !== true)
              }
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {selectedIsSeatDecrease ? 'Schedule Seat Decrease' : 'Schedule Downgrade'}
                </>
              )}
            </Button>
            ) : pricingStep === 'review' ? (
            // Plain upgrade / new-subscription path -- advance to the Payment step
            // (billing address + card) rather than submitting straight from Review.
            <Button
              className={`${pricingStepIndex > 0 ? 'col-span-2 sm:col-span-1' : 'sm:col-span-2'} h-14 text-base`}
              onClick={() => setPricingStep('payment')}
              disabled={
                checkoutLoading
                || quoteLoading
                || Boolean(seatCountBlocker)
                || Boolean(clientCheckoutBlocker)
                || checkoutQuote?.can_checkout === false
              }
            >
              Next
            </Button>
            ) : clientSecret ? (
            <Button
              className={`${pricingStepIndex > 0 ? 'col-span-2 sm:col-span-1' : 'sm:col-span-2'} h-14 text-base`}
              onClick={handleConfirmPayment}
              disabled={paymentSubmitting}
            >
              {paymentSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay Now
                </>
              )}
            </Button>
            ) : (
            <Button
              className={`${pricingStepIndex > 0 ? 'col-span-2 sm:col-span-1' : 'sm:col-span-2'} h-14 text-base`}
              onClick={handleCheckout}
              disabled={
                checkoutLoading
                || quoteLoading
                || Boolean(seatCountBlocker)
                || Boolean(clientCheckoutBlocker)
                || checkoutQuote?.can_checkout === false
                || (needsAddressInput && !addressElementComplete)
              }
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Continue to Checkout
                </>
              )}
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
